/**
 * Site-wide engagement tracking — delegated listeners installed once at app startup (see
 * index.tsx) so every page gets click + scroll-depth tracking automatically, without each
 * component wiring its own handlers. Complements the existing per-action calls like
 * analyticsService.trackButtonClick — some overlap between the two is fine (different
 * eventType values), this is the broad safety net, not a replacement.
 */
import analyticsService from '../services/analyticsService';

const SCROLL_MILESTONES = [25, 50, 75, 100];

let scrollPathForMilestones = null;
let firedMilestones = new Set();
let scrollTicking = false;

/** Best available human-readable label for a clicked element, cheapest signal first. */
function resolveClickLabel(el) {
  const trackLabel = el.getAttribute('data-track-label');
  if (trackLabel) return trackLabel;
  const ariaLabel = el.getAttribute('aria-label');
  if (ariaLabel && ariaLabel.trim()) return ariaLabel.trim().slice(0, 80);
  // innerText (not textContent) — textContent pulls in text from nested <style>/<script> tags
  // (e.g. inline `<style>{...}</style>` used for CSS keyframe animations on some cards), which
  // aren't visible content and would otherwise show up as raw CSS in the click label.
  const text = (el.innerText || '').trim().replace(/\s+/g, ' ');
  if (text) return text.slice(0, 80);
  if (el.tagName === 'IMG' && el.alt) return el.alt.slice(0, 80);
  return el.tagName.toLowerCase();
}

function handleDocumentClick(event) {
  const target = event.target.closest?.('button, a, [role="button"], [data-track-label]');
  if (!target) return;
  const label = resolveClickLabel(target);
  if (!label) return;
  analyticsService.trackClick(label, window.location.pathname);
}

function handleScroll() {
  if (scrollTicking) return;
  scrollTicking = true;
  requestAnimationFrame(() => {
    scrollTicking = false;
    const path = window.location.pathname;
    if (scrollPathForMilestones !== path) {
      scrollPathForMilestones = path;
      firedMilestones = new Set();
    }
    const doc = document.documentElement;
    const scrollableHeight = doc.scrollHeight - doc.clientHeight;
    if (scrollableHeight <= 0) return; // page doesn't scroll — nothing to measure
    const pct = Math.min(100, Math.round((window.scrollY / scrollableHeight) * 100));
    for (const milestone of SCROLL_MILESTONES) {
      if (pct >= milestone && !firedMilestones.has(milestone)) {
        firedMilestones.add(milestone);
        analyticsService.trackScrollDepth(milestone, path);
      }
    }
  });
}

/** Call once at app startup — safe no-op on the server (no window/document). */
export function initSiteEngagementTracking() {
  if (typeof window === 'undefined') return;
  document.addEventListener('click', handleDocumentClick, { capture: true });
  window.addEventListener('scroll', handleScroll, { passive: true });
}
